import api from 'src/redux/config';

// Toutes les fonctions ci-dessous passent par le point d'entrée réseau central
// (redux/config.js) : le jeton de connexion et la langue sont déjà ajoutés
// automatiquement à chaque requête, pas besoin de les gérer ici.

/**
 * Récupérer toutes les notifications par type (kind)
 * @param {string} kind - Type: 'company', 'suggestion', 'subscription', 'payment-proof'
 * @returns {Promise<Array>}
 */
export const getNotificationsByKind = async (kind) => {
  const response = await api.get(`/notification/kind/${kind}`);
  return response.data;
};

/**
 * Récupérer toutes les notifications pour le receiver (utilisateur connecté)
 * @returns {Promise<Array>}
 */
export const getAllNotificationsByReceiver = async () => {
  const response = await api.get('/notification/receiver');
  return response.data;
};

/**
 * Récupérer les 11 dernières notifications (avec nettoyage automatique)
 * @returns {Promise<Array>}
 */
export const getLast11Notifications = async () => {
  const response = await api.get('/notification/receiver/last11');
  return response.data;
};

/**
 * Marquer une notification comme lue
 * @param {string} notificationId - ID de la notification
 * @returns {Promise<Object>}
 */
export const markNotificationAsRead = async (notificationId) => {
  const response = await api.put(`/notification/${notificationId}`, {});
  return response.data;
};

/**
 * Supprimer une notification
 * @param {string} notificationId - ID de la notification
 * @returns {Promise<void>}
 */
export const deleteNotification = async (notificationId) => {
  await api.delete(`/notification/${notificationId}`);
};

/**
 * Récupérer les notifications groupées par claim (pour les messages)
 * Réservé aux CLIENT
 * @returns {Promise<Array>}
 */
export const getMsgNotificationsGrouped = async () => {
  const response = await api.get('/notification/receiver/msg/grouped');
  return response.data;
};

/**
 * Récupérer les notifications msg non lues pour un claim
 * @param {string} claimId - ID du claim
 * @returns {Promise<Array>}
 */
export const getUnreadMsgByClaimId = async (claimId) => {
  const response = await api.get(`/notification/claims/${claimId}/msg/unread`);
  return response.data;
};

/**
 * Marquer toutes les notifications msg comme lues pour un claim
 * @param {string} claimId - ID du claim
 * @returns {Promise<Object>}
 */
export const markMsgAsReadByClaimId = async (claimId) => {
  const response = await api.put(`/notification/claims/${claimId}/msg/view`, {});
  return response.data;
};

const DEVICE_ID_STORAGE_KEY = 'fcm_device_id';

// Identifiant stable pour CE navigateur, généré une seule fois et conservé dans
// localStorage. Sans lui, le backend ne peut reconnaître "même appareil, nouveau
// jeton" (son propre code sait le faire via `deviceId`, mais seulement si on le
// lui envoie) : à chaque rotation du jeton FCM par Google, une entrée en plus
// s'ajoutait indéfiniment dans fcmTokens (9 entrées constatées pour un seul
// compte) au lieu de remplacer la précédente — d'où des jetons périmés jamais
// nettoyés et des envois parfois dupliqués/instables sur le même appareil.
function getOrCreateDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (!id) {
    id = window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, id);
  }
  return id;
}

/**
 * Enregistrer le jeton FCM de ce navigateur (notifications push, même onglet fermé)
 * Le champ `platform` est volontairement omis : réservé côté backend à
 * 'android'/'ios' aujourd'hui, l'envoyer casserait la requête (validation
 * stricte). Le laisser vide fait retomber le serveur sur 'android' par
 * défaut, sans incidence sur la livraison réelle (Google identifie le jeton).
 * @param {string} fcmToken
 * @returns {Promise<boolean>}
 */
export const saveFcmToken = async (fcmToken) => {
  // Appel en arrière-plan : s'il échoue, l'utilisateur n'a rien à faire, donc
  // pas de message global d'erreur (voir serverErrorNotifier).
  const response = await api.post('/user/save-fcm-token', {
    fcmToken,
    deviceId: getOrCreateDeviceId(),
  }, { skipGlobalErrorToast: true });
  return response.data;
};

export default {
  getNotificationsByKind,
  getAllNotificationsByReceiver,
  getLast11Notifications,
  markNotificationAsRead,
  deleteNotification,
  getMsgNotificationsGrouped,
  getUnreadMsgByClaimId,
  markMsgAsReadByClaimId,
  saveFcmToken,
};
